import { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";

const PORTAL_PATHS = ["/dashboard", "/onboarding", "/retreat-welcome", "/community", "/handbook", "/compatibility", "/arrival", "/guide"];

/**
 * Blocking overlay shown to guests who are still on their admin-issued
 * starting password. They must set their own password before continuing.
 */
export const ForcePasswordChange = () => {
  const { user, token, updateUser } = useAuth();
  const { pathname } = useLocation();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || user.role === "admin" || !user.must_change_password) return null;
  if (!PORTAL_PATHS.some((p) => pathname.startsWith(p))) return null;

  const submit = async () => {
    if (next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/guest/password/initial`,
        { new_password: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser({ must_change_password: false });
      toast.success("Password set — it's all yours now 🔐");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't set password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl" data-testid="force-password-modal">
        <div className="w-14 h-14 rounded-full bg-[#F4F1DE] flex items-center justify-center mb-5">
          <KeyRound className="w-7 h-7 text-[#E07A5F]" />
        </div>
        <h2 className="font-serif text-2xl text-[#3D4035] mb-2">Set your own password</h2>
        <p className="text-sm text-[#3D4035]/60 mb-6 leading-relaxed">
          You're using a starting password. For your security, pick your own — takes 10 seconds.
        </p>
        <div className="space-y-3">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="New password (min 6 characters)"
              className="w-full h-12 px-4 pr-12 rounded-xl border border-stone-200 bg-[#FBF7F1] text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/30"
              data-testid="force-new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#3D4035]/40 hover:text-[#3D4035]/70"
              aria-label={showPw ? "Hide passwords" : "Show passwords"}
              data-testid="force-toggle-password-visibility"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <input
            type={showPw ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="w-full h-12 px-4 rounded-xl border border-stone-200 bg-[#FBF7F1] text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/30"
            data-testid="force-confirm-password-input"
          />
          <Button
            onClick={submit}
            disabled={saving}
            className="w-full h-12 bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full font-semibold"
            data-testid="force-password-submit-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save my password"}
          </Button>
        </div>
      </div>
    </div>
  );
};
